import {createExpandedRowModel, stockFeatures, tableFeatures, useTable} from '@tanstack/react-table'
import {useCallback, useMemo} from 'react'

import {MIN_CELL_WIDTH} from '../../components/table/constants'
import type {TableDataType} from '../../components/table/table-data-type'
import {isRowSelectable} from '../../components/table/use-row-selection'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import type {ColumnModel} from '../../models/column-model'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'
import {getRowId} from './helpers/get-row-id'
import {type ColumnDef, memexTableFeature, type Row} from './helpers/types'
import {useColumnResizing} from './hooks/use-column-resizing'
import {useColumnSizing} from './hooks/use-column-sizing'
import {controlledStateErrorWarnings, useControlledTableState} from './hooks/use-controlled-table-state'
import {RowDraggerColumnDef} from './providers/columns/row-dragger-column-def'
import {useTableColumns} from './providers/columns/use-table-columns'
import {getCustomGroupedRowModel} from './row-models/get-custom-grouped-row-model'

type TableProps = {
  fields: ReadonlyArray<ColumnModel>
  pinnedRightColumnDefs?: ReadonlyArray<ColumnDef<TableDataType>>
}

const features = tableFeatures({
  ...stockFeatures,
  memexTableFeature,
  expandedRowModel: createExpandedRowModel(),
  groupedRowModel: getCustomGroupedRowModel(),
})

export function useReactTable({fields, pinnedRightColumnDefs}: TableProps) {
  const readonlyData = useReactTableData()
  // Replace with a mutable array while maintaining referential equality checks.
  const data = useMemo(() => [...readonlyData], [readonlyData])
  const {columnDefinitions, columnVisibility} = useTableColumns()
  const {columnResizing, onColumnResizingChange} = useColumnResizing()
  const {columnSizing, onColumnSizingChange} = useColumnSizing()
  const {hierarchyEnabled} = useTableHierarchy()

  const pinnedLeftColumnDefs: ReadonlyArray<ColumnDef<TableDataType>> = useMemo(() => [RowDraggerColumnDef], [])
  const allColumnDefs = useMemo(
    () => [...pinnedLeftColumnDefs, ...columnDefinitions, ...(pinnedRightColumnDefs ?? [])],
    [columnDefinitions, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  )

  const {expanded, grouping, groupQueryData, sorting, subIssuesQueryData} = useControlledTableState()
  const {copilotTasksEnabled} = subIssuesQueryData
  const columnOrder = useMemo(
    () => [
      ...pinnedLeftColumnDefs.map(definition => definition.id),
      ...fields.map(field => field.id.toString()),
      ...(pinnedRightColumnDefs?.map(definition => definition.id) ?? []),
    ],
    [fields, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  )

  const getRowCanExpand = useCallback(
    (row: Row<TableDataType>) => {
      if (row.getIsGrouped()) return true
      if (!hierarchyEnabled && !copilotTasksEnabled) return false
      if (hierarchyEnabled) {
        const subIssuesProgress = row.original.getSubIssuesProgress()
        if (subIssuesProgress && subIssuesProgress.total > 0) return true
      }
      return copilotTasksEnabled && row.subRows.some(subRow => subRow.agentAssignmentTaskData)
    },
    [hierarchyEnabled, copilotTasksEnabled],
  )

  const table = useTable({
    features,
    columns: allColumnDefs,
    data,
    getRowId,
    getRowCanExpand: hierarchyEnabled || copilotTasksEnabled ? getRowCanExpand : undefined,
    enableRowSelection: isRowSelectable,
    manualSorting: true,
    columnResizeMode: 'onChange',
    defaultColumn: {minSize: MIN_CELL_WIDTH},
    autoResetAll: false,
    groupedColumnMode: false,
    initialState: {
      columnPinning: {
        start: pinnedLeftColumnDefs.map(definition => definition.id),
        end: pinnedRightColumnDefs?.map(definition => definition.id) ?? [],
      },
      groupQueryData,
      subIssuesQueryData,
    },
    state: {
      columnVisibility,
      columnOrder,
      columnSizing,
      columnResizing,
      expanded,
      grouping,
      groupQueryData,
      sorting,
      subIssuesQueryData,
    },
    onColumnSizingChange,
    onColumnResizingChange,
    ...controlledStateErrorWarnings,
  })

  const getState = useCallback(() => table.store.state, [table.store])
  return useMemo(() => ({...table, getState}), [getState, table])
}

function useReactTableData() {
  const {items} = useMemexItems()
  return useMemo(() => items, [items])
}
